import {
  Injectable,
  inject,
} from '@angular/core';
import {FlowableApiService} from '../../api/api/flowable-api.service';
import {FlowableConstants} from '../../../../../common/constants/flowable/flowable-constants';
import {FlowableApiUrlService} from '../../api/api-url/flowable-api-url.service';
import {ApiUrlService} from '../../../../infrastructure/api/core/api-url/api-url.service';

@Injectable({
  providedIn: 'root',
})
export class FlowableVariablesService {

  private readonly apiUrlService: ApiUrlService = inject(ApiUrlService);

  private readonly flwApiService: FlowableApiService = inject(FlowableApiService);

  private readonly flwApiUrlService: FlowableApiUrlService = inject(FlowableApiUrlService);

  /**
   * Method: Initializes window flowable frontend configuration.
   * It fetches the Flowable frontend configuration, sets it to the window object
   * and initializes the window flowable endpoints.
   */
  public async initWindowFlowableFrontendConfiguration(): Promise<void> {
    const flwFrontendConfiguration = await this.getFlowableFrontendConfiguration();
    if (flwFrontendConfiguration?.endpoints) {
      this.setWindowFlowableFrontendConfiguration(flwFrontendConfiguration);
      this.initWindowFlowableEndpoints(flwFrontendConfiguration);
    } else {
      this.setDefaultWindowFlowableEndpoint();
    }
  }

  /**
   * Method: Initializes the window flowable endpoints.
   * It first checks if the window flowable endpoints are already set, and if not,
   * it sets them using the frontend configuration.
   * @param flwFrontendConfiguration - The frontend configuration.
   */
  public initWindowFlowableEndpoints(flwFrontendConfiguration: any): void {
    this.checkWindowFlowableEndpoints();
    this.setFlowableEndpoint(flwFrontendConfiguration);
  }

  /**
   * Method: Sets the window flowable endpoints based on the frontend configuration.
   * If the frontend configuration contains endpoints, it sets the window flowable endpoints
   * using the setWindowFlowableEndpoints method. Otherwise, it sets the custom window
   * flowable endpoints using the setCustomWindowFlowableEndpoint method.
   * @param flwFrontendConfiguration - The frontend configuration containing.
   */
  public setFlowableEndpoint(flwFrontendConfiguration: any): void {
    const endpoints = flwFrontendConfiguration?.endpoints || null;
    if (endpoints) {
      this.setWindowFlowableEndpoints(endpoints);
    }

    this.setCustomWindowFlowableEndpoint();
  }

  /**
   * Method: Sets default window flowable endpoints.
   * This method initializes the window flowable endpoints by setting the 'baseUrl' and 'sobradoUrl'
   * using the Flowable API services. It also configures additional custom endpoints by calling
   * the setCustomWindowFlowableEndpoint method.
   */
  public setDefaultWindowFlowableEndpoint(): void {
    this.checkWindowFlowableEndpoints();
    this.setWindowFlowableEndpointByName('baseUrl', this.flwApiService.getFlowableUrl(''));
    this.setWindowFlowableEndpointByName('sobradoUrl', this.apiUrlService.sobradoUrl);
    this.setCustomWindowFlowableEndpoint();
  }

  /**
   * Method: Sets custom window flowable endpoints for platform and content.
   * This method configures the window flowable endpoints by calling setWindowFlowableEndpointByName
   * with specific endpoint names and their corresponding URLs obtained from the Flowable API services.
   */
  public setCustomWindowFlowableEndpoint(): void {
    this.flwApiUrlService.flwEndpointMap.forEach(endpoint => {
      this.setWindowFlowableEndpointByName(endpoint.name, this.flwApiService.getFlowableUrl(endpoint.apiUrl));
    });
  }

  /**
   * Method: Sets the value of a window flowable endpoint by name.
   * @param name - The name of the endpoint.
   * @param value - The value to set for the endpoint.
   */
  public setWindowFlowableEndpointByName(name: string, value: string): void {
    // Check if window flowable endpoints exist
    this.checkWindowFlowableEndpoints();
    // Set the value for the specified endpoint
    (window as any).flowable.endpoints[name] = value;
  }

  /**
   * Method: Sets the value of all window flowable endpoints.
   * @param value - The value to set for the endpoints.
   */
  public setWindowFlowableEndpoints(value: any): void {
    // Check if window flowable endpoints exist
    this.checkWindowFlowableEndpoints();
    // Set the value for the specified endpoints
    (window as any).flowable.endpoints = value;
  }

  /**
   * Method: Merge the frontend configuration into window flowable
   * @param value - Frontend configuration fetched from Flowable
   */
  public setWindowFlowableFrontendConfiguration(value: any): void {
    this.checkWindowFlowableVariable();
    (window as any).flowable = {...(window as any).flowable, ...value};
  }

  /**
   * Method: Resets the specified endpoint by name.
   * @param name - The name of the endpoint to reset.
   */
  public resetWindowFlowableEndpointByName(name: string): void {
    // Ensure window flowable endpoints are initialized
    this.checkWindowFlowableEndpoints();
    // Reset the specified endpoint by name
    (window as any).flowable.endpoints[name] = '';
  }

  /**
   * Method: Checks and sets up the flowable for the window
   */
  private checkWindowFlowableVariable(): void {
    // Assigns an empty object to the window.flowable if it's undefined
    (window as any).flowable = (window as any).flowable || {};
  }

  /**
   * Checks and sets up the endpoints for the Flowable API
   */
  private checkWindowFlowableEndpoints(): void {
    this.checkWindowFlowableVariable();
    (window as any).flowable.endpoints = (window as any).flowable.endpoints || {};
  }

  /**
   * Set the current endpoint before path
   * @param path - The path to set
   */
  public setEndpointCurrentBeforePath(path: string): void {
    this.setWindowFlowableEndpointByName(FlowableConstants.CURRENT_BEFORE_PATH, path);
  }

  /**
   * Method: Gets the frontend configuration.
   * @returns A Promise that resolves with the configuration or null if not found.
   */
  public getFlowableFrontendConfiguration(): Promise<any> {
    return this.flwApiService.flwGetRequestWrapper(this.flwApiUrlService.flwFrontendConfigurationApi);
  }

  /**
   * Method: Gets the window flowable endpoints.
   * @returns The window flowable endpoints.
   */
  public getWindowFlowableEndpoints(): any {
    return (window as any)?.flowable?.endpoints;
  }

}
